import React, { useState, useEffect, useCallback } from 'react'
import Recomendations from '../home/Recomendations'
import Spiner from '../../components/modals/Spiner'
import productsLogic from '../../functions/logic/productsLogic'
import t from '../../translations/i18n'
import generalLogic from '../../functions/logic/generalLogic'
import { useSnackbar } from 'notistack'
import userLogic from '../../functions/logic/userLogic'
import '../../scss/shopping-cart.scss'

export default function ShoppingCart() {
  const [cartItems, setCartItems] = useState([])
  const [load, setLoad] = useState(false)
  const [prod, setProd] = useState(true)
  const [prodInfo, setProdInfo] = useState([])
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [shippingCost, setShippingCost] = useState(0)
  const [discountCode, setDiscountCode] = useState('')
  const [discountApplied, setDiscountApplied] = useState(false)
  const [discountAmount, setDiscountAmount] = useState(0)
  const [shippings, setShippings] = useState([])
  const [selectedShipping, setSelectedShipping] = useState('')
  const [ship, setEnvio] = useState(0)
  const [allShippings, setAllShippings] = useState([])
  const { enqueueSnackbar } = useSnackbar()
  const FREE_SHIPPING_CODE = 'TIPENV0007'
  const FREE_SHIPPING_MINIMUM = 60
  const DEFAULT_SHIPPING_CODE = 'TIPENV0001'

  const getProducts = async () => {
    const products = await productsLogic.getProductsLogic(0, '')

    if (products.success && products.data.length > 0) {
      setProd(true)
      setProdInfo(products.data)
    }
  }

  useEffect(() => {
    setLoad(true)

    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || []

    const validatedCartItems = storedCartItems.map((item) => ({
      ...item,
      price: parseFloat(item.price) || 0,
      quantity: parseInt(item.quantity) || 1,
      tarifa: parseFloat(item.tarifa) || 0
    }))

    setCartItems(validatedCartItems)

    const storedDiscountCode = localStorage.getItem('discountCode')
    const storedDiscountAmount = localStorage.getItem('discountAmount')

    if (storedDiscountCode && storedDiscountAmount) {
      setDiscountCode(storedDiscountCode)
      setDiscountApplied(true)
      setDiscountAmount(parseFloat(storedDiscountAmount))
    }

    const storedShippingCost = localStorage.getItem('shippingCost')

    if (storedShippingCost) {
      setShippingCost(parseFloat(storedShippingCost))
    }

    getProducts()
    getShippingMethods()

    const token = localStorage.getItem('authToken')
    setIsLoggedIn(!!token)

    setLoad(false)
  }, [])

  const getShippingMethods = async () => {
    setLoad(true)

    try {
      const shipping = await generalLogic.getShippingMethods()

      if (shipping.success && shipping.data.length > 0) {
        setAllShippings(shipping.data)

        const normalShippings = shipping.data.filter(
          (method) => method.nemonicoTiposEnvio !== FREE_SHIPPING_CODE
        )

        setShippings(normalShippings)

        const defaultShipping =
          normalShippings.find(
            (method) => method.nemonicoTiposEnvio === DEFAULT_SHIPPING_CODE
          ) || normalShippings[0]

        setSelectedShipping(defaultShipping || null)
        setEnvio(parseFloat(defaultShipping?.costo) || 0)
        setShippingCost(parseFloat(defaultShipping?.costo) || 0)

        localStorage.setItem(
          'shippingCost',
          defaultShipping?.nemonicoTiposEnvio || DEFAULT_SHIPPING_CODE
        )
      }
    } finally {
      setLoad(false)
    }
  }

  const handleQuantityChange = async (index, newQuantity) => {
    setLoad(true)

    const updatedCartItems = [...cartItems]

    updatedCartItems[index].quantity = newQuantity

    const ivaRate = updatedCartItems[index].tarifa

    let iva = 0

    if (ivaRate !== 0) {
      iva = (
        ((updatedCartItems[index].price * ivaRate) / 100) *
        newQuantity
      ).toFixed(2)
    }

    const total = (
      updatedCartItems[index].price * newQuantity +
      parseFloat(iva)
    ).toFixed(2)

    updatedCartItems[index].iva = parseFloat(iva)
    updatedCartItems[index].total = parseFloat(total)

    if (isLoggedIn) {
      const response = await userLogic.saveViewCartLogic(updatedCartItems)

      setLoad(false)

      enqueueSnackbar(response.data.message, {
        variant: response.variant,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right'
        }
      })

      if (response.success) {
        setCartItems(updatedCartItems)
        localStorage.setItem('cartItems', JSON.stringify(updatedCartItems))
      }
    } else {
      setCartItems(updatedCartItems)
      localStorage.setItem('cartItems', JSON.stringify(updatedCartItems))
    }

    setLoad(false)
  }

  const handleRemoveItem = (index) => {
    setLoad(true)

    const updatedCartItems = cartItems.filter((_, i) => i !== index)

    setCartItems(updatedCartItems)

    localStorage.setItem('cartItems', JSON.stringify(updatedCartItems))

    userLogic.saveViewCartLogic(updatedCartItems)

    setLoad(false)
  }

  const calculateSubtotals = () => {
    let subtotalIVA15 = 0
    let subtotalIVA12 = 0
    let subtotalIVA0 = 0
    let totalIVA = 0

    if (Array.isArray(cartItems)) {
      cartItems.forEach((item) => {
        const itemPrice =
          parseFloat(item.precio_descuento) > 0
            ? parseFloat(item.precio_descuento)
            : parseFloat(item.price)

        const itemQuantity = parseInt(item.quantity) || 0

        const itemTarifa = parseFloat(item.tarifa) || 0

        const itemIVA = parseFloat(item.iva) || 0

        const subtotal = itemPrice * itemQuantity

        if (itemTarifa === 15) {
          subtotalIVA15 += subtotal
        } else if (itemTarifa === 12) {
          subtotalIVA12 += subtotal
        } else {
          subtotalIVA0 += subtotal
        }

        totalIVA += itemIVA * itemQuantity
      })
    }

    return {
      subtotalIVA15: subtotalIVA15.toFixed(2),
      subtotalIVA12: subtotalIVA12.toFixed(2),
      subtotalIVA0: subtotalIVA0.toFixed(2),
      totalIVA: totalIVA.toFixed(2)
    }
  }

  const calculateTax = () => {
    let totalTax = 0

    if (Array.isArray(cartItems)) {
      cartItems.forEach((item) => {
        const itemPrice =
          parseFloat(item.precio_descuento) > 0
            ? parseFloat(item.precio_descuento)
            : parseFloat(item.price) || 0

        const itemQuantity = parseInt(item.quantity) || 0

        const itemTarifa = parseFloat(item.tarifa) || 0

        if (itemTarifa > 0) {
          totalTax += (itemPrice * itemQuantity * itemTarifa) / 100
        }
      })
    }

    return totalTax.toFixed(2)
  }

  const calculateTotal = () => {
    const { subtotalIVA15, subtotalIVA12, subtotalIVA0 } = calculateSubtotals()

    const tax = parseFloat(calculateTax()) || 0

    const shipping = parseFloat(shippingCost) || 0

    const discount = parseFloat(discountAmount) || 0

    return (
      parseFloat(subtotalIVA15) +
      parseFloat(subtotalIVA12) +
      parseFloat(subtotalIVA0) +
      tax +
      shipping -
      discount
    ).toFixed(2)
  }

  const x = 1000.0

  const calculateShippingThresholdTotal = useCallback(() => {
    let total = 0

    cartItems.forEach((item) => {
      const itemPrice =
        parseFloat(item.precio_descuento) > 0
          ? parseFloat(item.precio_descuento)
          : parseFloat(item.price) || 0

      const itemQuantity = parseInt(item.quantity) || 0

      total += itemPrice * itemQuantity
    })

    return total
  }, [cartItems])

  useEffect(() => {
    if (allShippings.length === 0) {
      return
    }

    const totalSinIVA = calculateShippingThresholdTotal()

    const freeShipping = allShippings.find(
      (method) => method.nemonicoTiposEnvio === FREE_SHIPPING_CODE
    )

    if (totalSinIVA >= FREE_SHIPPING_MINIMUM && freeShipping) {
      setShippings([freeShipping])
      setSelectedShipping(freeShipping)
      setEnvio(parseFloat(freeShipping.costo) || 0)
      setShippingCost(parseFloat(freeShipping.costo) || 0)

      localStorage.setItem('shippingCost', FREE_SHIPPING_CODE)

      return
    }

    const normalShippings = allShippings.filter(
      (method) => method.nemonicoTiposEnvio !== FREE_SHIPPING_CODE
    )

    setShippings(normalShippings)

    const defaultShipping =
      normalShippings.find(
        (method) => method.nemonicoTiposEnvio === DEFAULT_SHIPPING_CODE
      ) || normalShippings[0]

    setSelectedShipping(defaultShipping)
    setEnvio(parseFloat(defaultShipping?.costo) || 0)
    setShippingCost(parseFloat(defaultShipping?.costo) || 0)

    localStorage.setItem(
      'shippingCost',
      defaultShipping?.nemonicoTiposEnvio || DEFAULT_SHIPPING_CODE
    )
  }, [cartItems, discountAmount, allShippings, calculateShippingThresholdTotal])

  const total = parseFloat(calculateTotal())
  const excedeLimite = total > x

  /*
   * Se mantiene exactamente la misma lógica.
   * Ahora el event.target.value viene del <select>.
   */
  const handleShippingChange = (event) => {
    setLoad(true)

    const selectedId = event.target.value

    const selected = shippings.find((method) => method.id === selectedId)

    if (selected) {
      setSelectedShipping(selected)
      setEnvio(parseFloat(selected.costo))
      setShippingCost(selected.costo)

      localStorage.setItem('shippingCost', selected.nemonicoTiposEnvio)

      const updatedCartItems = localStorage.getItem('cartItems')

      userLogic.saveViewCartLogic(updatedCartItems)
    }

    setLoad(false)
  }

  const applyDiscount = async (code) => {
    setLoad(true)

    let discountPercentage = 0

    const discount = await generalLogic.getDiscountLogic(code)

    if (discount.success && discount.data.length > 0) {
      discountPercentage = parseFloat(discount.data[0]['descuento'])

      if (discountPercentage > 0) {
        const updatedCartItems = cartItems.map((item) => {
          const itemPrice = parseFloat(item.price) || 0

          const itemTarifa = parseFloat(item.tarifa) || 0

          const itemQuantity = parseInt(item.quantity) || 0

          const discountedPrice =
            itemPrice - (itemPrice * discountPercentage) / 100

          const itemIVA =
            itemTarifa > 0 ? discountedPrice * (itemTarifa / 100) : 0

          const total = (discountedPrice + itemIVA) * itemQuantity

          return {
            ...item,
            discountedPrice: discountedPrice.toFixed(2),
            iva: itemIVA.toFixed(2),
            total: total.toFixed(2)
          }
        })

        setCartItems(updatedCartItems)
        setDiscountCode(code)
        setDiscountApplied(true)
        setDiscountAmount(discountPercentage)

        localStorage.setItem('discountCode', code)

        localStorage.setItem('discountAmount', discountPercentage.toString())
      } else {
        setDiscountApplied(false)
        setDiscountAmount(0)

        localStorage.removeItem('discountCode')

        localStorage.removeItem('discountAmount')
      }
    } else {
      enqueueSnackbar('No existe el código de oferta ingresado', {
        variant: 'warning',
        anchorOrigin: {
          vertical: global.SNACKBARVER,
          horizontal: global.SNACKBARHOR
        }
      })
    }

    setLoad(false)
  }

  const handleDiscountChange = (e) => {
    const code = e.target.value
    setDiscountCode(code)
  }

  const handleApplyDiscount = () => {
    applyDiscount(discountCode)
  }

  if (cartItems.length === 0) {
    return (
      <>
        <div className="easybuy-empty-cart-page">
          <div className="container">
            <div className="easybuy-empty-cart">
              <div className="easybuy-empty-cart-icon">
                <i className="la la-shopping-cart"></i>
              </div>

              <h2>No tienes productos en tu carrito</h2>

              <p>Pero tenemos algunas opciones que podrían interesarte.</p>
            </div>

            {prod && <Recomendations t={t} data={prodInfo} />}
          </div>
        </div>
      </>
    )
  }

  return (
    <main className="main easybuy-cart-page">
      <Spiner opt={load} />

      <div className="page-content">
        <div className="container">
          <div className="easybuy-cart-header"></div>

          {discountApplied && (
            <div className="easybuy-discount-alert">
              <div className="easybuy-discount-icon">
                <i className="la la-tag"></i>
              </div>

              <div>
                <strong>¡Descuento aplicado!</strong>

                <span>
                  Has utilizado el código <b>{discountCode}</b>.
                </span>
              </div>
            </div>
          )}

          <div className="row easybuy-cart-layout">
            <div className="col-lg-8">
              <div className="easybuy-cart-products-card">
                <div className="easybuy-cart-card-header">
                  <div>
                    <h3>Tus productos</h3>
                    <span>Productos seleccionados</span>
                  </div>

                  <div className="easybuy-free-shipping-notice">
                    <i className="la la-truck"></i>

                    <span>
                      ¡Compra <strong>$60 o más</strong> y recibe envío gratis!
                    </span>
                  </div>
                </div>

                <div className="easybuy-cart-table-wrapper">
                  <table className="easybuy-cart-table">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Precio</th>
                        <th>Cantidad</th>
                        <th>Subtotal</th>
                        <th>IVA</th>
                        <th>Total</th>
                        <th></th>
                      </tr>
                    </thead>

                    <tbody>
                      {cartItems.map((item, index) => {
                        const itemPrice = parseFloat(item.price) || 0

                        const discountPrice =
                          parseFloat(item.precio_descuento) || itemPrice

                        const totalDiscountPrice = (
                          discountPrice * item.quantity
                        ).toFixed(2)

                        const iva = (
                          totalDiscountPrice *
                          (parseFloat(item.tarifa) / 100)
                        ).toFixed(2)

                        const totalWithTax = (
                          parseFloat(totalDiscountPrice) + parseFloat(iva)
                        ).toFixed(2)

                        const hasPromotion = Boolean(
                          Number(item.valor_descuento) > 0 &&
                            Number(item.tarifa_descuento) > 0 &&
                            Number(item.precio_descuento) > 0
                        )

                        return (
                          <tr key={index}>
                            <td className="easybuy-product-cell">
                              <div className="easybuy-product">
                                <div className="easybuy-product-image">
                                  {hasPromotion && (
                                    <span className="easybuy-product-badge">
                                      Oferta
                                    </span>
                                  )}

                                  <img
                                    src={`${item.imageUrl}`}
                                    alt={item.name}
                                  />
                                </div>

                                <div className="easybuy-product-info">
                                  <h3>{item.name}</h3>

                                  {hasPromotion && (
                                    <span className="easybuy-promotion-text">
                                      Precio especial
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td className="easybuy-price-cell">
                              {hasPromotion ? (
                                <div className="easybuy-price">
                                  <span className="easybuy-old-price">
                                    ${itemPrice.toFixed(2)}
                                  </span>

                                  <strong>${discountPrice.toFixed(2)}</strong>
                                </div>
                              ) : (
                                <strong>${itemPrice.toFixed(2)}</strong>
                              )}
                            </td>

                            <td className="easybuy-quantity-cell">
                              <div className="easybuy-quantity">
                                <input
                                  type="number"
                                  className="form-control"
                                  value={item.quantity}
                                  min="1"
                                  max="10"
                                  step="1"
                                  required
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      index,
                                      parseInt(e.target.value)
                                    )
                                  }
                                />
                              </div>
                            </td>

                            <td className="easybuy-money-cell">
                              ${totalDiscountPrice}
                            </td>

                            <td className="easybuy-money-cell">${iva}</td>

                            <td className="easybuy-total-cell">
                              ${totalWithTax}
                            </td>

                            <td className="easybuy-remove-cell">
                              <button
                                type="button"
                                className="easybuy-remove-button"
                                onClick={() => handleRemoveItem(index)}
                                title="Eliminar producto"
                              >
                                <i className="la la-trash"></i>
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="easybuy-cart-bottom">
                  <div className="easybuy-coupon">
                    <div className="easybuy-coupon-icon">
                      <i className="la la-tag"></i>
                    </div>

                    <div className="easybuy-coupon-content">
                      <strong>¿Tienes un código de descuento?</strong>

                      <span>Ingresa tu código para aplicar una promoción.</span>
                    </div>

                    <div className="easybuy-coupon-form">
                      <input
                        type="text"
                        className="form-control"
                        value={discountCode}
                        onChange={handleDiscountChange}
                        placeholder="Código de descuento"
                      />

                      <button type="button" onClick={handleApplyDiscount}>
                        Aplicar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <aside className="col-lg-4">
              <div className="easybuy-summary-card">
                <div className="easybuy-summary-header">
                  <span>Resumen</span>

                  <h3>Resumen de compra</h3>
                </div>

                <div className="easybuy-summary-body">
                  <div className="easybuy-summary-row">
                    <span>Subtotal IVA 15%</span>

                    <strong>${calculateSubtotals().subtotalIVA15}</strong>
                  </div>

                  <div className="easybuy-summary-row">
                    <span>Subtotal IVA 0%</span>

                    <strong>${calculateSubtotals().subtotalIVA0}</strong>
                  </div>

                  <div className="easybuy-summary-row">
                    <span>Total IVA</span>

                    <strong>${calculateTax()}</strong>
                  </div>

                  <div className="easybuy-summary-divider"></div>

                  {/* MÉTODO DE ENVÍO */}

                  <div className="easybuy-shipping-section">
                    <div className="easybuy-summary-section-title">
                      <i className="la la-truck"></i>

                      <span>Método de envío</span>
                    </div>

                    <div className="easybuy-shipping-select-wrapper">
                      <select
                        className="easybuy-shipping-select"
                        value={selectedShipping?.id || ''}
                        onChange={handleShippingChange}
                      >
                        <option value="" disabled>
                          Selecciona un método de envío
                        </option>

                        {shippings.map((method) => (
                          <option key={method.id} value={method.id}>
                            {method.nombre} — $
                            {parseFloat(method.costo).toFixed(0)}
                            {' · '}
                            {method.dias} días
                          </option>
                        ))}
                      </select>

                      <i className="la la-angle-down easybuy-shipping-select-icon"></i>
                    </div>

                    {selectedShipping && (
                      <div className="easybuy-selected-shipping-info">
                        <div>
                          <i className="la la-check-circle"></i>

                          <span>{selectedShipping.nombre}</span>
                        </div>

                        <small>
                          ${parseFloat(selectedShipping.costo).toFixed(0)}
                          {' · '}
                          {selectedShipping.dias} días
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="easybuy-summary-row">
                    <span>Envío</span>

                    <strong>${ship.toFixed(2)}</strong>
                  </div>

                  {discountApplied && (
                    <div className="easybuy-summary-row easybuy-discount-row">
                      <span>Descuento</span>

                      <strong>
                        -$
                        {discountAmount.toFixed(2)}
                      </strong>
                    </div>
                  )}

                  <div className="easybuy-summary-divider"></div>

                  <div className="easybuy-summary-total">
                    <span>Total</span>

                    <strong>${calculateTotal()}</strong>
                  </div>

                  {isLoggedIn && (
                    <>
                      {excedeLimite && (
                        <div className="easybuy-limit-warning">
                          <i className="la la-exclamation-circle"></i>

                          <span>
                            El cupo límite de compra es ${x.toFixed(2)}.
                          </span>
                        </div>
                      )}

                      {!excedeLimite && (
                        <button
                          className="easybuy-checkout-button"
                          onClick={() => {
                            setLoad(true)

                            const BASENAME = ''

                            window.location.href = BASENAME + global.CHECKOUT
                          }}
                        >
                          <span>Continuar con el pago</span>
                          <i className="la la-arrow-right"></i>
                        </button>
                      )}
                    </>
                  )}

                  {!isLoggedIn && (
                    <div className="easybuy-login-warning">
                      <i className="la la-lock"></i>

                      <p>Por favor, inicia sesión para proceder con el pago.</p>
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </main>
  )
}
